import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { css } from '@compiled/react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { TriggerProps } from '@atlaskit/popup/types';
import { updatePageRovoParams } from '@atlaskit/rovo-triggers';

import { releaseRemixHandoffHolds } from '@atlassian/malleable-ui-components/state/machine/remix-handoff-hold';

import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';
import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { LoadableBackground, LoadableLazy } from '@confluence/loadable';
import type {
	ObjectSidebarRemixLifecycleAdapter,
	ObjectSidebarRemixLifecycleEndReason,
	ObjectSidebarRemixEntryPath,
	ObjectSidebarRemixLifecyclePhase,
	RetainedObjectSidebarRemixLifecycle,
} from '@confluence/malleable-ui-common/entry-points/ObjectSidebarRemixLifecycle.ts';
import {
	admitAndRetainObjectSidebarRemixLifecycle,
	isObjectSidebarRemixLifecycleEnabled,
	useIsObjectSidebarRemixStartBlocked,
	useObjectSidebarRemixLifecycleOwner,
} from '@confluence/malleable-ui-common/entry-points/ObjectSidebarRemixLifecycle.ts';
import { useObjectSidebarRemixLifecycleDiagnostics } from '@confluence/malleable-ui-common/entry-points/useObjectSidebarRemixLifecycleDiagnostics.ts';
import type {
	PageRemixCloseOptions,
	PageRemixLifecycleAdmissionOutcome,
} from '@confluence/malleable-ui-common/entry-points/PageRemixContainer.ts';
import { usePageContentId } from '@confluence/page-context';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	loadObjectSidebarRemixAdf,
	type ObjectSidebarRemixAdfSource,
} from './objectSidebarRemixAdf.ts';

const remixObjectPagePopupLoader = () =>
	import(/* webpackChunkName: "loadable-RemixObjectPagePopup" */ './RemixObjectPagePopup.tsx');

const RemixObjectPagePopup = LoadableLazy({
	loader: async () => (await remixObjectPagePopupLoader()).RemixObjectPagePopup,
});

// Split out rather than imported directly: this provider is part of the eager
// ObjectSidebarControl chunk, and PageRemixSubmitOwner pulls in the conversation assistant.
// Background priority because the coordinator is invisible until the user submits in Rovo, so it
// must not hold the page-load metric — BACKGROUND is the one priority excluded from the UFO hold.
const PageRemixSubmitOwner = LoadableBackground({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PageRemixSubmitOwner" */ '@confluence/malleable-ui-common/entry-points/PageRemixContainer.ts'
			)
		).PageRemixSubmitOwner,
});

const stablePopupTriggerStyle = css({
	position: 'absolute',
	width: 1,
	height: 1,
	overflow: 'hidden',
	pointerEvents: 'none',
});

export type ObjectSidebarRemixLifecycleAdmissionRequest = {
	conversationId?: string;
};

type RetainedObjectSidebarRemixLifecycleSession = {
	token: symbol;
	lifecycle: RetainedObjectSidebarRemixLifecycle;
};

export type ObjectSidebarRemixLifecycleAdmissionOutcome = 'retained' | 'legacy' | 'rejected';

type ObjectSidebarRemixPopupSession = {
	adfDocument: DocNode;
	loadAdfDocument: () => Promise<DocNode | null>;
	isModalOpen: boolean;
	isLifecycleTreatment: boolean;
	lifecycleToken: symbol | undefined;
	sessionToken: symbol;
	/** True from a preserving close until any terminal close (including an already-settled one). */
	isTransferPending: boolean;
};

type ObjectSidebarRemixLifecycleSessionContextValue = {
	hasAdapter: boolean;
	isLifecycleExperimentEnabled: () => boolean;
	isObjectSidebarRemixModalOpen: boolean;
	/**
	 * A Remix flow is active somewhere on the page (renderer, editor, or this sidebar), so new
	 * starts are unavailable. Treatment only. Never feed this into Stop, dismiss, or recovery.
	 */
	isRemixStartBlockedByActiveFlow: boolean;
	isRovoRemixStarted: boolean;
	markRovoRemixStarted: () => void;
	clearRovoRemixStarted: () => void;
	retainedLifecycleToken: symbol | undefined;
	admitLifecycle: (request?: ObjectSidebarRemixLifecycleAdmissionRequest) => boolean;
	admitObjectSidebarRemixLifecycle: (
		request?: ObjectSidebarRemixLifecycleAdmissionRequest,
	) => ObjectSidebarRemixLifecycleAdmissionOutcome;
	admitRetainedObjectSidebarRemixLifecycle: (
		request?: ObjectSidebarRemixLifecycleAdmissionRequest,
	) => PageRemixLifecycleAdmissionOutcome;
	openObjectSidebarRemixModal: (
		adfDocument: DocNode,
		loadAdfDocument: () => Promise<DocNode | null>,
		options: { isLifecycleTreatment: boolean },
	) => void;
	releaseCurrentRetainedLifecycle: (reason: ObjectSidebarRemixLifecycleEndReason) => void;
	releaseRetainedLifecycleForToken: (
		expectedToken: symbol | undefined,
		reason: ObjectSidebarRemixLifecycleEndReason,
	) => void;
	releaseRetainedLifecycleWithoutEnd: (expectedToken: symbol | undefined) => void;
	closeObjectSidebarRemixModal: (options?: PageRemixCloseOptions) => void;
	stopObjectSidebarRemixThinking: (options?: { reopenModal?: boolean }) => void;
	observeLegacyPhase: (
		token: symbol | undefined,
		phase: ObjectSidebarRemixLifecyclePhase,
		entryPath?: ObjectSidebarRemixEntryPath,
	) => void;
};

const legacySession: ObjectSidebarRemixLifecycleSessionContextValue = {
	hasAdapter: false,
	isLifecycleExperimentEnabled: () => false,
	isObjectSidebarRemixModalOpen: false,
	isRemixStartBlockedByActiveFlow: false,
	isRovoRemixStarted: false,
	markRovoRemixStarted: () => {},
	clearRovoRemixStarted: () => {},
	retainedLifecycleToken: undefined,
	admitLifecycle: () => true,
	admitObjectSidebarRemixLifecycle: () => 'legacy',
	admitRetainedObjectSidebarRemixLifecycle: () => ({ status: 'legacy' }),
	openObjectSidebarRemixModal: () => {},
	releaseCurrentRetainedLifecycle: () => {},
	releaseRetainedLifecycleForToken: () => {},
	releaseRetainedLifecycleWithoutEnd: () => {},
	closeObjectSidebarRemixModal: () => {},
	stopObjectSidebarRemixThinking: () => {},
	observeLegacyPhase: () => {},
};

/**
 * The page the last mounted Object Sidebar session saw. Module scope, so it survives the remount
 * that navigating to another host tree causes (a folder, space overview or database, or straight
 * into editing another page).
 */
let lastSeenPageContentId: string | undefined;

const ObjectSidebarRemixLifecycleSessionContext =
	createContext<ObjectSidebarRemixLifecycleSessionContextValue>(legacySession);

export const ObjectSidebarRemixLifecycleSessionProvider = ({
	children,
	lifecycleAdapter,
	contentBodyValue,
	getAdfDocument,
	isRemixEntryEligible = false,
	isObjectSidebarExpanded = false,
}: {
	children: React.ReactNode;
	lifecycleAdapter?: ObjectSidebarRemixLifecycleAdapter;
	/** Whether the Object Sidebar Remix entry point is available for this content. */
	isRemixEntryEligible?: boolean;
	/** Whether the Object Sidebar action bar is currently expanded. */
	isObjectSidebarExpanded?: boolean;
} & ObjectSidebarRemixAdfSource): React.JSX.Element => {
	const rightSidebarContext = useRightSidebarContext();
	const { width: rightSidebarWidth } = rightSidebarContext.getCurrent() || {};
	const { openConversationAssistant, isConversationAssistantSidebarActive, mode } =
		useToggleConversationAssistant();
	const lifecycleOwner = useObjectSidebarRemixLifecycleOwner();
	const { isLoggedIn, isRovoLLMEnabled, accessStatus } = useSessionData();
	// Match App.jsx: only mount conversation-assistant when Rovo chat is allowed for this user.
	// Without this, variant2 eagerly mounts PageRemixSubmitOwner on tenants where Rovo is off,
	// which fetches conversation history, 404s, and shows a misleading toast on page load.
	const isRovoEnabled =
		isRovoLLMEnabled && isLoggedIn && accessStatus !== AccessStatus.EXTERNAL_COLLABORATOR_ACCESS;
	/**
	 * Latched on the first expand, never cleared.
	 *
	 * The coordinator has to outlive the Remix button, which unmounts whenever the Object Sidebar
	 * collapses — that is the submit-dropping bug this host exists to fix. But hosting it
	 * unconditionally would mount the conversation assistant, and its history fetch, on every
	 * view-page and edit-page load even for people who never open the sidebar. Latching keeps both
	 * properties: nothing is paid until the sidebar is opened once, and a later collapse cannot
	 * unmount the coordinator.
	 *
	 * Starts false so the server never renders the coordinator, which also keeps this branch out of
	 * the SSR/hydration comparison. The button cannot be clicked before the first expand, so there
	 * is no submit to miss while this is still false.
	 */
	const [hasObjectSidebarBeenExpanded, setHasObjectSidebarBeenExpanded] = useState(false);
	useEffect(() => {
		if (isObjectSidebarExpanded) {
			setHasObjectSidebarBeenExpanded(true);
		}
	}, [isObjectSidebarExpanded]);
	const shouldHostRovoRemixOwner =
		isRemixEntryEligible &&
		isRovoEnabled &&
		hasObjectSidebarBeenExpanded &&
		UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant2');
	const retainedLifecycleRef = useRef<RetainedObjectSidebarRemixLifecycleSession | undefined>(
		undefined,
	);
	const [retainedLifecycleToken, setRetainedLifecycleToken] = useState<symbol | undefined>();
	const [isRovoRemixStarted, setIsRovoRemixStarted] = useState(false);
	const markRovoRemixStarted = useCallback(() => setIsRovoRemixStarted(true), []);
	const clearRovoRemixStarted = useCallback(() => setIsRovoRemixStarted(false), []);

	/**
	 * Clears the started flag when the Rovo panel closes.
	 *
	 * Closing the Rovo panel publishes nothing to subscribe to, so the only way to notice it is to
	 * watch whether the panel is showing. The same click that starts the Remix also asks the panel
	 * to open, so a close is only acted on once the panel has been seen open at least once.
	 *
	 * This lives here rather than in the Remix button because the flag lives here. The button
	 * unmounts whenever the Object Sidebar collapses, so an observer there would miss a close that
	 * happened while collapsed and the button would come back still looking selected.
	 */
	const isRovoPanelOpen = isConversationAssistantSidebarActive || mode !== undefined;
	const hasSeenRovoPanelOpenRef = useRef(false);
	useEffect(() => {
		if (isRovoPanelOpen) {
			hasSeenRovoPanelOpenRef.current = true;
			return;
		}
		if (!hasSeenRovoPanelOpenRef.current) {
			return;
		}
		hasSeenRovoPanelOpenRef.current = false;
		clearRovoRemixStarted();
		// Closing Rovo Chat also stops holding Remix for a generation handed off to it.
		releaseRemixHandoffHolds('object-sidebar', 'panel-closed');
	}, [clearRovoRemixStarted, isRovoPanelOpen]);
	// A handed-off generation belongs to the page it started on, so stop holding Remix for it once an
	// Object Sidebar is on another page. Not on unmount: switching between view and edit mounts a
	// new Object Sidebar tree for the same page, and the watcher and timers still end the hold.
	// An unknown id (e.g. mid route change) is skipped, so it neither releases nor hides a change.
	const [pageContentId] = usePageContentId();
	useEffect(() => {
		if (pageContentId === undefined) {
			return;
		}
		if (lastSeenPageContentId !== undefined && lastSeenPageContentId !== pageContentId) {
			releaseRemixHandoffHolds('object-sidebar', 'left-page');
		}
		lastSeenPageContentId = pageContentId;
	}, [pageContentId]);
	// One no-exposure read per mounted Rovo owner decides the Rovo-sidebar submit path.
	// Exposure is recorded once at admission in the lifecycle adapter.
	const isLifecycleTreatmentEnabledForRovoOwnerRef = useRef<boolean | undefined>(undefined);
	if (isLifecycleTreatmentEnabledForRovoOwnerRef.current === undefined) {
		isLifecycleTreatmentEnabledForRovoOwnerRef.current = isObjectSidebarRemixLifecycleEnabled();
	}
	const isLifecycleTreatmentEnabledForRovoOwner =
		isLifecycleTreatmentEnabledForRovoOwnerRef.current;
	// Read at render, like the click-time check in the button, so the affordance and the start
	// path agree. Only the treatment decides this, so it matches the Rovo page picker, which
	// cannot see this sidebar's lifecycle adapter.
	const isRemixStartBlockedByActiveFlow = useIsObjectSidebarRemixStartBlocked(
		isObjectSidebarRemixLifecycleEnabled(),
	);
	const [activePopupSession, setActivePopupSession] = useState<
		ObjectSidebarRemixPopupSession | undefined
	>();
	const activePopupSessionRef = useRef<ObjectSidebarRemixPopupSession | undefined>(undefined);
	const commitActivePopupSession = useCallback(
		(popupSession: ObjectSidebarRemixPopupSession | undefined) => {
			activePopupSessionRef.current = popupSession;
			setActivePopupSession(popupSession);
		},
		[],
	);
	const isConversationAssistantSidebarActiveRef = useRef(isConversationAssistantSidebarActive);
	isConversationAssistantSidebarActiveRef.current = isConversationAssistantSidebarActive;
	const reportLifecycleDiagnostic = useObjectSidebarRemixLifecycleDiagnostics();
	const reportParityObserverFailure = useCallback(
		(operation: 'recordLegacyParityPhase' | 'bindParityObservationToken') => {
			try {
				reportLifecycleDiagnostic('failed', { operation });
			} catch {
				// Observer and analytics failures must never affect the existing lifecycle.
			}
		},
		[reportLifecycleDiagnostic],
	);
	const observeLegacyPhase = useCallback(
		(
			token: symbol | undefined,
			phase: ObjectSidebarRemixLifecyclePhase,
			entryPath?: ObjectSidebarRemixEntryPath,
		) => {
			try {
				lifecycleAdapter?.observeLegacyPhase?.(token, phase, entryPath);
			} catch {
				reportParityObserverFailure('recordLegacyParityPhase');
				// Parity observation must never affect the existing lifecycle.
			}
		},
		[lifecycleAdapter, reportParityObserverFailure],
	);
	const observePageRemixLegacyPhase = useCallback(
		(
			phase: ObjectSidebarRemixLifecyclePhase,
			entryPath?: ObjectSidebarRemixEntryPath,
			token?: symbol,
		) => observeLegacyPhase(token, phase, entryPath),
		[observeLegacyPhase],
	);
	const logOwnerDestroyedWithActiveFlow = useCallback(
		(transferPending: boolean) => {
			reportLifecycleDiagnostic('observed', {
				operation: 'ownerDestroyedWithActiveFlow',
				terminalReason: 'cancelled',
				...(transferPending ? { transferPending: true as const } : {}),
			});
		},
		[reportLifecycleDiagnostic],
	);
	const logOwnerDestroyedWithActiveFlowRef = useRef(logOwnerDestroyedWithActiveFlow);
	logOwnerDestroyedWithActiveFlowRef.current = logOwnerDestroyedWithActiveFlow;
	const logDuplicateTerminalAttempt = useCallback(
		(reason: ObjectSidebarRemixLifecycleEndReason) => {
			reportLifecycleDiagnostic('observed', {
				operation: 'duplicateTerminalAttempt',
				terminalReason: reason,
			});
		},
		[reportLifecycleDiagnostic],
	);

	const releaseCurrentRetainedLifecycle = useCallback(
		(reason: ObjectSidebarRemixLifecycleEndReason) => {
			const retainedLifecycle = retainedLifecycleRef.current;
			if (!retainedLifecycle) {
				return;
			}
			retainedLifecycleRef.current = undefined;
			setRetainedLifecycleToken(undefined);
			retainedLifecycle.lifecycle.settleTerminal();
			retainedLifecycle.lifecycle.end(reason);
		},
		[],
	);

	const releaseRetainedLifecycleForToken = useCallback(
		(expectedToken: symbol | undefined, reason: ObjectSidebarRemixLifecycleEndReason) => {
			const retainedLifecycle = retainedLifecycleRef.current;
			if (!expectedToken) {
				return;
			}
			if (!retainedLifecycle || retainedLifecycle.token !== expectedToken) {
				logDuplicateTerminalAttempt(reason);
				return;
			}
			retainedLifecycleRef.current = undefined;
			setRetainedLifecycleToken(undefined);
			retainedLifecycle.lifecycle.settleTerminal();
			retainedLifecycle.lifecycle.end(reason);
		},
		[logDuplicateTerminalAttempt],
	);

	const releaseRetainedLifecycleWithoutEnd = useCallback((expectedToken: symbol | undefined) => {
		const retainedLifecycle = retainedLifecycleRef.current;
		if (!expectedToken || !retainedLifecycle || retainedLifecycle.token !== expectedToken) {
			return;
		}
		// The owner already released on the accepted INSERTED; only the session's token remains.
		retainedLifecycleRef.current = undefined;
		setRetainedLifecycleToken(undefined);
	}, []);

	const admitObjectSidebarRemixLifecycle = useCallback(
		(request: ObjectSidebarRemixLifecycleAdmissionRequest = {}) => {
			// The lifecycle adapter owns rollout and exposure at the Object Sidebar Remix start
			// boundary. Disabled/control outcomes fall through to the legacy path.
			const lifecycleRetention = admitAndRetainObjectSidebarRemixLifecycle({
				// Admission consumes only startFlow; the Object Sidebar adapter replaces the
				// shared observeLegacyPhase callback with its sidebar-specific signature.
				adapter: lifecycleAdapter ? { startFlow: lifecycleAdapter.startFlow } : undefined,
				owner: lifecycleOwner,
				request: {
					kind: 'create',
					...(request.conversationId !== undefined && { conversationId: request.conversationId }),
				},
			});
			if (
				lifecycleRetention.status === 'active-flow-rejected' ||
				lifecycleRetention.status === 'infrastructure-failure'
			) {
				return 'rejected';
			}
			if (lifecycleRetention.status === 'retained') {
				const token = Symbol('object-sidebar-remix-lifecycle');
				try {
					lifecycleAdapter?.bindObservationToken?.(lifecycleRetention.lifecycle.remixId, token);
				} catch {
					reportParityObserverFailure('bindParityObservationToken');
					// A parity observer must never affect lifecycle retention.
				}
				retainedLifecycleRef.current = {
					token,
					lifecycle: lifecycleRetention.lifecycle,
				};
				setRetainedLifecycleToken(token);
				const popupSession = activePopupSessionRef.current;
				if (popupSession) {
					commitActivePopupSession({ ...popupSession, lifecycleToken: token });
				}
				return 'retained';
			}
			return 'legacy';
		},
		[commitActivePopupSession, lifecycleAdapter, lifecycleOwner, reportParityObserverFailure],
	);

	const admitRetainedObjectSidebarRemixLifecycle = useCallback(
		(
			request: ObjectSidebarRemixLifecycleAdmissionRequest = {},
		): PageRemixLifecycleAdmissionOutcome => {
			const status = admitObjectSidebarRemixLifecycle(request);
			if (status !== 'retained') {
				return { status };
			}
			const retained = retainedLifecycleRef.current;
			if (!retained?.lifecycle.conversationId) {
				return { status: 'rejected' };
			}
			const { token, lifecycle } = retained;
			return {
				status,
				conversationId: lifecycle.conversationId,
				lifecycle,
				observationToken: token,
				remixId: lifecycle.remixId,
				transition: (event) => lifecycle.transition(event),
				isCurrent: () =>
					retainedLifecycleRef.current?.token === token && (lifecycle.isCurrent?.() ?? true),
			};
		},
		[admitObjectSidebarRemixLifecycle],
	);

	const admitLifecycle = useCallback(
		(request: ObjectSidebarRemixLifecycleAdmissionRequest = {}) =>
			admitObjectSidebarRemixLifecycle(request) !== 'rejected',
		[admitObjectSidebarRemixLifecycle],
	);

	const rovoRemixAdfSourceRef = useRef<ObjectSidebarRemixAdfSource>({
		contentBodyValue,
		getAdfDocument,
	});
	rovoRemixAdfSourceRef.current = { contentBodyValue, getAdfDocument };
	const loadRovoRemixAdfDocument = useCallback(
		() => loadObjectSidebarRemixAdf(rovoRemixAdfSourceRef.current),
		[],
	);

	/**
	 * Terminal handler for the Rovo-sidebar coordinator.
	 *
	 * Uses the request-scoped token supplied by PageRemixModal when present. Legacy close callers
	 * do not have a token, so those use the current retained lifecycle.
	 */
	const closeRovoRemixOwner = useCallback(
		(options?: PageRemixCloseOptions) => {
			const hasRequestToken =
				options !== undefined && Object.prototype.hasOwnProperty.call(options, 'observationToken');
			const retainedLifecycle = retainedLifecycleRef.current;
			const retainedToken = hasRequestToken ? options?.observationToken : retainedLifecycle?.token;
			if (options?.preserveRemixLifecycle) {
				// Intentional transfer into the staging preview: the owner UI hides, the lifecycle stays.
				clearRovoRemixStarted();
				return;
			}
			if (!retainedToken) {
				if (!hasRequestToken) {
					clearRovoRemixStarted();
				}
				return;
			}
			const release = (token: symbol) => {
				if (options?.remixLifecycleAlreadySettled) {
					// The machine already tore down on an accepted INSERTED; drop the token without `end`.
					releaseRetainedLifecycleWithoutEnd(token);
					return;
				}
				releaseRetainedLifecycleForToken(token, options?.remixLifecycleEndReason ?? 'cancelled');
			};
			if (hasRequestToken && retainedLifecycle?.token !== retainedToken) {
				release(retainedToken);
				return;
			}
			clearRovoRemixStarted();
			release(retainedToken);
		},
		[clearRovoRemixStarted, releaseRetainedLifecycleForToken, releaseRetainedLifecycleWithoutEnd],
	);

	const stopRovoRemixThinking = useCallback(() => {
		clearRovoRemixStarted();
	}, [clearRovoRemixStarted]);

	const openObjectSidebarRemixModal = useCallback(
		(
			adfDocument: DocNode,
			loadAdfDocument: () => Promise<DocNode | null>,
			{ isLifecycleTreatment }: { isLifecycleTreatment: boolean },
		) => {
			commitActivePopupSession({
				adfDocument,
				loadAdfDocument,
				isLifecycleTreatment,
				isModalOpen: true,
				lifecycleToken: retainedLifecycleRef.current?.token,
				sessionToken: Symbol('object-sidebar-remix-popup-session'),
				isTransferPending: false,
			});
		},
		[commitActivePopupSession],
	);

	const closeObjectSidebarRemixModal = useCallback(
		(options?: PageRemixCloseOptions) => {
			const popupSession = activePopupSessionRef.current;
			if (!popupSession) {
				return;
			}
			commitActivePopupSession({
				...popupSession,
				isModalOpen: false,
				isTransferPending: Boolean(options?.preserveRemixLifecycle),
			});
			if (options?.preserveRemixLifecycle) {
				return;
			}
			if (options?.remixLifecycleAlreadySettled) {
				releaseRetainedLifecycleWithoutEnd(popupSession.lifecycleToken);
				return;
			}
			const reason = options?.remixLifecycleEndReason ?? 'cancelled';
			if (popupSession.lifecycleToken) {
				releaseRetainedLifecycleForToken(popupSession.lifecycleToken, reason);
			} else {
				releaseCurrentRetainedLifecycle(reason);
			}
		},
		[
			commitActivePopupSession,
			releaseCurrentRetainedLifecycle,
			releaseRetainedLifecycleForToken,
			releaseRetainedLifecycleWithoutEnd,
		],
	);

	const closeObjectSidebarRemixModalForSession = useCallback(
		(expectedSessionToken: symbol, options?: PageRemixCloseOptions) => {
			const popupSession = activePopupSessionRef.current;
			if (!popupSession || popupSession.sessionToken !== expectedSessionToken) {
				if (options?.remixLifecycleEndReason) {
					logDuplicateTerminalAttempt(options.remixLifecycleEndReason);
				}
				return;
			}
			commitActivePopupSession({
				...popupSession,
				isModalOpen: false,
				isTransferPending: Boolean(options?.preserveRemixLifecycle),
			});
			if (options?.preserveRemixLifecycle) {
				return;
			}
			if (options?.remixLifecycleAlreadySettled) {
				releaseRetainedLifecycleWithoutEnd(popupSession.lifecycleToken);
				return;
			}
			const reason = options?.remixLifecycleEndReason ?? 'cancelled';
			if (popupSession.lifecycleToken) {
				releaseRetainedLifecycleForToken(popupSession.lifecycleToken, reason);
			} else {
				releaseCurrentRetainedLifecycle(reason);
			}
		},
		[
			commitActivePopupSession,
			logDuplicateTerminalAttempt,
			releaseCurrentRetainedLifecycle,
			releaseRetainedLifecycleForToken,
			releaseRetainedLifecycleWithoutEnd,
		],
	);

	const stopObjectSidebarRemixThinking = useCallback(
		(options?: { reopenModal?: boolean }) => {
			if (!options?.reopenModal) {
				return;
			}
			const popupSession = activePopupSessionRef.current;
			if (popupSession) {
				commitActivePopupSession({ ...popupSession, isModalOpen: true });
			}
		},
		[commitActivePopupSession],
	);

	const stopObjectSidebarRemixThinkingForSession = useCallback(
		(expectedSessionToken: symbol, options?: { reopenModal?: boolean }) => {
			if (!options?.reopenModal) {
				return;
			}
			const popupSession = activePopupSessionRef.current;
			if (popupSession?.sessionToken === expectedSessionToken) {
				commitActivePopupSession({ ...popupSession, isModalOpen: true });
			}
		},
		[commitActivePopupSession],
	);

	const handleOpenSidebarConversation = useCallback(
		(conversationId: string) => {
			updatePageRovoParams({ pathway: 'chat', conversationId });
			if (!isConversationAssistantSidebarActiveRef.current) {
				openConversationAssistant('sidebar');
			}
		},
		[openConversationAssistant],
	);

	const renderPopupTrigger = useCallback(({ ref, ...triggerProps }: TriggerProps) => {
		return <span {...triggerProps} ref={ref} css={stablePopupTriggerStyle} aria-hidden="true" />;
	}, []);

	useEffect(() => {
		return () => {
			const retainedLifecycle = retainedLifecycleRef.current;
			if (!retainedLifecycle) {
				return;
			}
			retainedLifecycleRef.current = undefined;
			logOwnerDestroyedWithActiveFlowRef.current(
				activePopupSessionRef.current?.isTransferPending ?? false,
			);
			retainedLifecycle.lifecycle.end('cancelled');
		};
	}, []);

	const value = useMemo(
		() => ({
			hasAdapter: lifecycleAdapter !== undefined,
			isLifecycleExperimentEnabled: isObjectSidebarRemixLifecycleEnabled,
			isObjectSidebarRemixModalOpen: activePopupSession?.isModalOpen ?? false,
			isRemixStartBlockedByActiveFlow,
			isRovoRemixStarted,
			markRovoRemixStarted,
			clearRovoRemixStarted,
			retainedLifecycleToken,
			admitLifecycle,
			admitObjectSidebarRemixLifecycle,
			admitRetainedObjectSidebarRemixLifecycle,
			openObjectSidebarRemixModal,
			releaseCurrentRetainedLifecycle,
			releaseRetainedLifecycleForToken,
			releaseRetainedLifecycleWithoutEnd,
			closeObjectSidebarRemixModal,
			stopObjectSidebarRemixThinking,
			observeLegacyPhase,
		}),
		[
			admitLifecycle,
			admitObjectSidebarRemixLifecycle,
			admitRetainedObjectSidebarRemixLifecycle,
			activePopupSession?.isModalOpen,
			clearRovoRemixStarted,
			closeObjectSidebarRemixModal,
			isRemixStartBlockedByActiveFlow,
			isRovoRemixStarted,
			lifecycleAdapter,
			markRovoRemixStarted,
			openObjectSidebarRemixModal,
			releaseCurrentRetainedLifecycle,
			releaseRetainedLifecycleForToken,
			releaseRetainedLifecycleWithoutEnd,
			retainedLifecycleToken,
			stopObjectSidebarRemixThinking,
			observeLegacyPhase,
		],
	);

	return (
		<ObjectSidebarRemixLifecycleSessionContext.Provider value={value}>
			{children}
			{shouldHostRovoRemixOwner ? (
				<PageRemixSubmitOwner
					loadAdfDocument={loadRovoRemixAdfDocument}
					onOpenSidebarConversation={handleOpenSidebarConversation}
					onClose={closeRovoRemixOwner}
					onStopThinking={stopRovoRemixThinking}
					onAdmitRemixLifecycle={lifecycleAdapter ? admitLifecycle : undefined}
					onAdmitRetainedRemixLifecycle={
						lifecycleAdapter && isLifecycleTreatmentEnabledForRovoOwner
							? admitRetainedObjectSidebarRemixLifecycle
							: undefined
					}
					isRemixLifecycleTreatmentEnabled={isLifecycleTreatmentEnabledForRovoOwner}
					shouldWaitForRemixHandoffBeforeClose={retainedLifecycleToken !== undefined}
					onObserveLegacyPhase={observePageRemixLegacyPhase}
				/>
			) : null}
			{activePopupSession ? (
				<RemixObjectPagePopup
					adfDocument={activePopupSession.adfDocument}
					loadAdfDocument={activePopupSession.loadAdfDocument}
					isModalOpen={activePopupSession.isModalOpen}
					isRemixStartBlocked={isRemixStartBlockedByActiveFlow}
					onClose={(options: PageRemixCloseOptions | undefined) =>
						closeObjectSidebarRemixModalForSession(activePopupSession.sessionToken, options)
					}
					onStopThinking={(options: { reopenModal?: boolean } | undefined) =>
						stopObjectSidebarRemixThinkingForSession(activePopupSession.sessionToken, options)
					}
					popupRepositionKey={rightSidebarWidth}
					onOpenSidebarConversation={handleOpenSidebarConversation}
					onAdmitRetainedRemixLifecycle={admitRetainedObjectSidebarRemixLifecycle}
					isRemixLifecycleTreatmentEnabled={activePopupSession.isLifecycleTreatment}
					shouldWaitForRemixHandoffBeforeClose={activePopupSession.lifecycleToken !== undefined}
					onObserveLegacyPhase={observePageRemixLegacyPhase}
					trigger={renderPopupTrigger}
				/>
			) : null}
		</ObjectSidebarRemixLifecycleSessionContext.Provider>
	);
};

export const useObjectSidebarRemixLifecycleSession =
	(): ObjectSidebarRemixLifecycleSessionContextValue =>
		useContext(ObjectSidebarRemixLifecycleSessionContext);
